import { useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { supabase } from '@/lib/supabase'
import { DealStatusBadge } from '../components/DealStatusBadge'
import type { DealStatus } from '../components/DealStatusBadge'

export const Route = createFileRoute('/deals')({
  component: DealsComponent,
})

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Profile {
  id: string
  email: string
  display_name: string | null
}

interface DealRow {
  id: string
  case_number: string
  status: DealStatus
  assigned_to: string | null
  created_by: string
  notes: string | null
  created_at: string
  updated_at: string
  assignee: Pick<Profile, 'id' | 'email' | 'display_name'> | null
  creator: Pick<Profile, 'id' | 'email' | 'display_name'>
  auction: {
    property_address: string | null
    surplus: number | null
    auction_date: string
    county: string | null
  } | null
}

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

function useDeals(statusFilter: string, assigneeFilter: string) {
  return useQuery<DealRow[]>({
    queryKey: ['deals', statusFilter, assigneeFilter],
    queryFn: async () => {
      let q = supabase
        .from('deals')
        .select(`
          id, case_number, status, assigned_to, created_by, notes, created_at, updated_at,
          auction:auctions!deals_case_number_fkey(property_address, surplus, auction_date, county),
          assignee:profiles!deals_assigned_to_fkey(id, email, display_name),
          creator:profiles!deals_created_by_fkey(id, email, display_name)
        `)
        .order('created_at', { ascending: false })

      if (statusFilter) q = q.eq('status', statusFilter)
      if (assigneeFilter) q = q.eq('assigned_to', assigneeFilter)

      const { data, error } = await q
      if (error) throw new Error(error.message)
      return (data ?? []) as DealRow[]
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  })
}

function useProfiles() {
  return useQuery<Profile[]>({
    queryKey: ['profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, display_name')
        .order('display_name')
      if (error) throw new Error(error.message)
      return (data ?? []) as Profile[]
    },
    staleTime: 10 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number | null): string {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const [year, month, day] = iso.split('-')
  if (!month) return iso
  return `${month}/${day}/${year}`
}

function displayName(profile: Pick<Profile, 'email' | 'display_name'> | null): string {
  if (!profile) return '—'
  return profile.display_name || profile.email
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'on_hold', label: 'On Hold' },
]

function DealsComponent() {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()

  const [statusFilter, setStatusFilter] = useState('')
  const [assigneeFilter, setAssigneeFilter] = useState('')

  const { data: deals, isLoading: dealsLoading, error: dealsError } = useDeals(statusFilter, assigneeFilter)
  const { data: profiles } = useProfiles()

  useEffect(() => {
    if (!isLoading && !user) {
      navigate({ to: '/login' })
    }
  }, [user, isLoading, navigate])

  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-gray-900">Deals</h2>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={assigneeFilter}
            onChange={e => setAssigneeFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white text-gray-700"
          >
            <option value="">All Assignees</option>
            {(profiles ?? []).map(p => (
              <option key={p.id} value={p.id}>{p.display_name || p.email}</option>
            ))}
          </select>
        </div>
      </div>

      {dealsLoading && (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500">Loading deals...</p>
        </div>
      )}

      {dealsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">Error loading deals: {(dealsError as Error).message}</p>
        </div>
      )}

      {!dealsLoading && !dealsError && (!deals || deals.length === 0) && (
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <p className="text-gray-500">No deals yet. Create one from a case detail page.</p>
        </div>
      )}

      {!dealsLoading && !dealsError && deals && deals.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-200">
            <span className="text-sm text-gray-500">
              {deals.length} deal{deals.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Case Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Auction Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Surplus</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Assigned To</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Created By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Created</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {deals.map(deal => (
                  <tr
                    key={deal.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate({ to: '/deals/$dealId', params: { dealId: deal.id } })}
                  >
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 whitespace-nowrap">{deal.case_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{deal.auction?.property_address ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(deal.auction?.auction_date ?? null)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-green-700 whitespace-nowrap">{formatCurrency(deal.auction?.surplus ?? null)}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><DealStatusBadge status={deal.status} /></td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{displayName(deal.assignee)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{displayName(deal.creator)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(deal.created_at.split('T')[0])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
